import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Container,
  Avatar,
  Stack,
  Button,
} from "@mui/material";
import { GitHub, LinkedIn } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGitlab } from "@fortawesome/free-brands-svg-icons";
import { useTranslation } from "react-i18next";
import Profilbild from "../images/Profilbild.jpg";
import HeaderData from "../data/HeaderData.json";

function HeaderContainer() {
  const { t } = useTranslation();
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const titles = t("header.titles", { returnObjects: true });
  const [titleIndex, setTitleIndex] = useState(0);

  useEffect(() => {
    const currentTitle = titles[titleIndex];
    if (currentIndex < currentTitle.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(currentTitle.substring(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, 100);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setCurrentIndex(0);
        setDisplayedText("");
        setTitleIndex((titleIndex + 1) % titles.length);
      }, 2000);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, titleIndex]);

  return (
    <Box
      id="home"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated orb 1 — top-left indigo */}
      <Box sx={{ position: "absolute", top: "-180px", left: "-180px", pointerEvents: "none" }}>
        <Box
          sx={{
            width: { xs: 400, md: 750 },
            height: { xs: 400, md: 750 },
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(99,102,241,0.22) 0%, transparent 70%)",
            animation: "orbFloat1 16s ease-in-out infinite",
            "@keyframes orbFloat1": {
              "0%, 100%": { transform: "translate(0, 0) scale(1)" },
              "33%": { transform: "translate(35px, -25px) scale(1.04)" },
              "66%": { transform: "translate(-20px, 30px) scale(0.97)" },
            },
          }}
        />
      </Box>

      {/* Animated orb 2 — bottom-right cyan */}
      <Box sx={{ position: "absolute", bottom: "0px", right: "-100px", pointerEvents: "none" }}>
        <Box
          sx={{
            width: { xs: 300, md: 550 },
            height: { xs: 300, md: 550 },
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(34,211,238,0.16) 0%, transparent 70%)",
            animation: "orbFloat2 22s ease-in-out infinite",
            "@keyframes orbFloat2": {
              "0%, 100%": { transform: "translate(0, 0) scale(1)" },
              "50%": { transform: "translate(-40px, -35px) scale(1.07)" },
            },
          }}
        />
      </Box>

      {/* Animated orb 3 — center accent */}
      <Box
        sx={{
          position: "absolute",
          top: "55%",
          left: "45%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            width: { xs: 200, md: 350 },
            height: { xs: 200, md: 350 },
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)",
            animation: "orbFloat3 13s ease-in-out infinite",
            "@keyframes orbFloat3": {
              "0%, 100%": { transform: "translate(0, 0) scale(1)" },
              "50%": { transform: "translate(25px, -20px) scale(1.1)" },
            },
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 6,
          }}
        >
          {/* Text Content */}
          <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
            {/* Available badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                mb: { xs: 1.5, md: 2 },
                px: 1.5,
                py: 0.5,
                borderRadius: "20px",
                border: "1px solid rgba(34,197,94,0.3)",
                bgcolor: "rgba(34,197,94,0.08)",
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "#22c55e",
                  flexShrink: 0,
                  animation: "pulse 2.2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.55, transform: "scale(1.4)" },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "#4ade80",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                }}
              >
                {t("header.available")}
              </Typography>
            </Box>

            <Typography
              variant="h5"
              sx={{
                color: "primary.light",
                fontWeight: 600,
                mb: { xs: 1, md: 1.5 },
                opacity: 0.85,
                fontSize: { xs: "1rem", sm: "1.25rem" },
                fontFamily: "monospace",
                letterSpacing: "0.05em",
                display: "block",
              }}
            >
              {t("header.greeting")}
            </Typography>

            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                mb: { xs: 1, md: 2 },
                fontSize: { xs: "2.2rem", sm: "2.8rem", md: "3.8rem" },
                background:
                  "linear-gradient(135deg, #f1f5f9 0%, #818cf8 55%, #22d3ee 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: 1.15,
              }}
            >
              {HeaderData.name}
            </Typography>

            <Typography
              variant="h4"
              sx={{
                color: "primary.light",
                mb: { xs: 2, md: 3 },
                fontWeight: 500,
                fontSize: { xs: "1rem", sm: "1.25rem", md: "1.6rem" },
                minHeight: { xs: "32px", sm: "40px", md: "50px" },
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              {displayedText}
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: "2px",
                  height: { xs: "20px", sm: "24px", md: "30px" },
                  bgcolor: "primary.light",
                  ml: 0.5,
                  animation: "blink 1s infinite",
                  "@keyframes blink": {
                    "0%, 50%": { opacity: 1 },
                    "51%, 100%": { opacity: 0 },
                  },
                }}
              />
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: { xs: 3, md: 3.5 },
                maxWidth: "520px",
                lineHeight: 1.8,
                mx: { xs: "auto", md: 0 },
                fontSize: { xs: "0.9rem", sm: "0.95rem" },
              }}
            >
              {t("header.location")}
            </Typography>

            {/* Social Icons */}
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                justifyContent: { xs: "center", md: "flex-start" },
                mb: { xs: 2.5, md: 3 },
              }}
            >
              {[
                {
                  href: HeaderData.github,
                  icon: <GitHub />,
                  hoverBg: "primary.main",
                },
                {
                  href: HeaderData.gitlab,
                  icon: <FontAwesomeIcon icon={faGitlab} />,
                  hoverBg: "#fc6d26",
                },
                {
                  href: HeaderData.linkedin,
                  icon: <LinkedIn />,
                  hoverBg: "#0077b5",
                },
              ].map(({ href, icon, hoverBg }, i) => (
                <IconButton
                  key={i}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "text.secondary",
                    bgcolor: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    "&:hover": {
                      bgcolor: hoverBg,
                      color: "#fff",
                      transform: "translateY(-4px)",
                      border: "1px solid transparent",
                    },
                    transition: "all 0.25s ease",
                  }}
                >
                  {icon}
                </IconButton>
              ))}
            </Stack>

            {/* CTA Button */}
            <Box sx={{ display: "flex", justifyContent: { xs: "center", md: "flex-start" } }}>
              <Button
                onClick={() =>
                  document
                    .getElementById("projects")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                variant="outlined"
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  color: "primary.light",
                  borderColor: "rgba(129,140,248,0.45)",
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: "primary.light",
                    bgcolor: "rgba(99,102,241,0.1)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(99,102,241,0.2)",
                  },
                  transition: "all 0.25s ease",
                }}
              >
                {t("header.cta")}
              </Button>
            </Box>
          </Box>

          {/* Profile Image */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: -8,
                  borderRadius: "50%",
                  background:
                    "conic-gradient(from 0deg, #6366f1, #22d3ee, #6366f1)",
                  opacity: 0.4,
                  animation: "spin 8s linear infinite",
                  "@keyframes spin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Avatar
                src={Profilbild}
                alt={HeaderData.name}
                sx={{
                  position: "relative",
                  width: { xs: 200, sm: 250, md: 340 },
                  height: { xs: 200, sm: 250, md: 340 },
                  border: "4px solid #09090b",
                  boxShadow:
                    "0 0 60px rgba(99,102,241,0.35), 0 0 120px rgba(99,102,241,0.1)",
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "scale(1.04)" },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Scroll-down hint */}
      <Box
        onClick={() =>
          document
            .getElementById("about")
            ?.scrollIntoView({ behavior: "smooth" })
        }
        sx={{
          position: "absolute",
          bottom: 32,
          left: "50%",
          transform: "translateX(-50%)",
          cursor: "pointer",
          opacity: 0.45,
          animation: "bounce 2s ease-in-out infinite",
          "@keyframes bounce": {
            "0%, 100%": { transform: "translateX(-50%) translateY(0)" },
            "50%": { transform: "translateX(-50%) translateY(-10px)" },
          },
          "&:hover": { opacity: 0.9 },
          transition: "opacity 0.2s",
        }}
      >
        <KeyboardArrowDownIcon
          sx={{ fontSize: 36, color: "primary.light" }}
        />
      </Box>
    </Box>
  );
}

export default HeaderContainer;
